import { useState, useEffect } from 'react';
// TEACHING_SCHEDULE_ASSIGNMENT_FIXES_20260916
import RichTextEditor from '../../../components/RichTextEditor';
import CourseDatePicker from './CourseDatePicker';
import {
  fetchAssignmentGroupLabelsForCourse,
  getAssignmentGroupDisplayName
} from '../../../utils/groupLabels';
import {
  expandInstructorAssignmentScheduleOccurrences,
  getScheduleItemDates,
  loadStudentAssignmentScheduleContext
} from '../../../utils/studentAssignmentSchedule';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;
const API_URL = process.env.REACT_APP_API_URL;

function makeReadingFileName(author, title, originalFileName) {
  const cleanAuthor = String(author || '')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const cleanTitle = String(title || '')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const original = String(originalFileName || '');
  const dotIndex = original.lastIndexOf('.');
  const extension =
    dotIndex > 0 && dotIndex < original.length - 1
      ? original.slice(dotIndex)
      : '';

  const stem = cleanAuthor
    ? `${cleanAuthor} - ${cleanTitle}`
    : cleanTitle;

  return `${stem}${extension}`;
}

async function fetchWeeks(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks?course_id=eq.${courseId}&select=*&order=week_number.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchHolidays(instructorId) {
  if (!instructorId) return [];

  const instructorResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/instructors?id=eq.${instructorId}&select=institution_id&limit=1`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!instructorResponse.ok) {
    console.warn('Could not load instructor institution.');
    return [];
  }

  const instructorRows = await instructorResponse.json();
  const institutionId =
    instructorRows?.[0]?.institution_id || null;

  if (!institutionId) return [];

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/holidays?institution_id=eq.${institutionId}&or=(is_personal.eq.false,instructor_id.eq.${instructorId})&select=*&order=date.asc`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    console.warn(
      'Could not load holidays:',
      await response.text()
    );
    return [];
  }

  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

async function fetchMeetings(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/student_meetings?course_id=eq.${courseId}&select=*&order=meeting_date.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchCourseAssignments(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignments?course_id=eq.${courseId}&select=*,assignment_types(name,requires_submission,assignment_kind)&order=instance_number.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchAssignmentTypeSettings(
  courseId
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_settings?course_id=eq.${courseId}&select=course_id,assignment_type_id,display_name,schedule_enabled,schedule_choice_enabled,schedule_open_offset_days,schedule_due_offset_days,group_deadlines_enabled`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization':
          'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      `Failed to load assignment settings (${response.status}): ${detail}`
    );
  }

  return response.json();
}

async function fetchAssignmentScheduleSelections(
  courseId
) {
  const response = await fetch(
    SUPABASE_URL +
      '/rest/v1/course_assignment_schedule_selections' +
      '?select=course_assignment_id,course_assignment_schedule_item_id,enrolment_id,group_number,course_assignments!inner(course_id)' +
      '&course_assignments.course_id=eq.' +
      encodeURIComponent(
        courseId
      ),
    {
      headers: {
        'apikey':
          SUPABASE_KEY,

        'Authorization':
          'Bearer ' +
          SUPABASE_KEY
      }
    }
  );


  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      'Failed to load assignment Schedule selections (' +
      response.status +
      '): ' +
      detail
    );
  }


  const data =
    await response.json();


  return Array.isArray(
    data
  )
    ? data
    : [];
}


async function fetchWeekContent(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_week_content?select=*,course_weeks!inner(course_id)&course_weeks.course_id=eq.${courseId}`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchCourseReadings(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_readings?course_id=eq.${courseId}&select=*&order=title.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchAllCourses() {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/courses?select=id,name,course_titles(name)&order=created_at.desc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchReadingsForCourse(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_readings?course_id=eq.${courseId}&select=*&order=title.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchWeekReadingAssignments(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_week_reading_assignments?select=*,course_readings(*),course_weeks!inner(id,course_id,week_number,class_date)&course_weeks.course_id=eq.${courseId}&order=display_order.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchCourseEvents(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_events?select=*,course_weeks!inner(id,course_id,week_number,class_date)&course_weeks.course_id=eq.${courseId}&order=event_date.asc,event_time.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );

  if (!response.ok) {
    throw new Error(`Failed to load course events (${response.status})`);
  }

  return response.json();
}

async function createCourseEvent(data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_events`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`Failed to create event (${response.status}): ${detail}`);
  }

  return response.json();
}

async function updateCourseEvent(id, data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_events?id=eq.${id}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({
        ...data,
        updated_at: new Date().toISOString()
      })
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`Failed to update event (${response.status}): ${detail}`);
  }

  return response.json();
}

async function deleteCourseEvent(id) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_events?id=eq.${id}`,
    {
      method: 'DELETE',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`Failed to remove event (${response.status}): ${detail}`);
  }
}

function sortCourseEvents(rows) {
  return [...rows].sort((a, b) => {
    const dateCompare = String(a.event_date || '').localeCompare(
      String(b.event_date || '')
    );

    if (dateCompare !== 0) return dateCompare;

    return String(a.event_time || '').localeCompare(
      String(b.event_time || '')
    );
  });
}

function hasMeaningfulRichText(html) {
  return String(html || '')
    .replace(/<br\s*\/?\s*>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim().length > 0;
}

async function saveWeekContent(courseWeekId, description) {
  const payload = {
    description,
    updated_at: new Date().toISOString()
  };

  const patchResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/course_week_content?course_week_id=eq.${courseWeekId}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(payload)
    }
  );

  if (!patchResponse.ok) {
    const detail = await patchResponse.text();
    throw new Error(
      `Failed to update week description (${patchResponse.status}): ${detail}`
    );
  }

  const patched = await patchResponse.json();
  if (patched?.[0]) return patched[0];

  const postResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/course_week_content`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({
        course_week_id: courseWeekId,
        ...payload
      })
    }
  );

  if (!postResponse.ok) {
    const detail = await postResponse.text();
    throw new Error(
      `Failed to create week description (${postResponse.status}): ${detail}`
    );
  }

  const created = await postResponse.json();
  return created?.[0] || {
    course_week_id: courseWeekId,
    ...payload
  };
}

async function deleteWeekContent(courseWeekId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_week_content?course_week_id=eq.${courseWeekId}`,
    {
      method: 'DELETE',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      `Failed to remove week description (${response.status}): ${detail}`
    );
  }
}

async function saveCourseReading(data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_readings`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to create reading record (${response.status})`);
  }

  const result = await response.json();
  if (!result?.[0]?.id) {
    throw new Error('Reading record was not created.');
  }
  return result;
}

async function updateCourseReading(readingId, data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_readings?id=eq.${encodeURIComponent(readingId)}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({
        title: data.title,
        author: data.author || null
      })
    }
  );

  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `Failed to update reading (${response.status}): ${detail}`
    );
  }

  const rows = await response.json();

  if (!rows?.[0]?.id) {
    throw new Error('Updated reading was not returned.');
  }

  return rows[0];
}

 async function assignReadingToWeek(courseWeekId, courseReadingId, displayOrder) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_week_reading_assignments`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({
        course_week_id: courseWeekId,
        course_reading_id: courseReadingId,
        display_order: displayOrder
      })
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to assign reading to week (${response.status})`);
  }

  const result = await response.json();
  if (!result?.[0]?.id) {
    throw new Error('Reading was uploaded but could not be assigned to the week.');
  }
  return result;
}

async function requestReadingDeletion(assignmentId) {
  await fetch(API_URL, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'deleteReading',
      assignmentId
    })
  });
}

async function readingAssignmentStillExists(assignmentId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_week_reading_assignments?id=eq.${assignmentId}&select=id`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    throw new Error(`Could not verify reading deletion (${response.status})`);
  }

  const rows = await response.json();
  return rows.length > 0;
}


async function fetchAssignmentScheduleReferences(
  itemType,
  sourceId
) {
  const field =
    itemType === 'reading'
      ? 'week_reading_assignment_id'
      : 'course_event_id';

  const headers = {
    apikey: SUPABASE_KEY,
    Authorization:
      'Bearer ' + SUPABASE_KEY
  };


  const [
    ordinaryResponse,
    structuredResponse
  ] =
    await Promise.all([
      fetch(
        SUPABASE_URL +
        '/rest/v1/course_assignment_schedule_items?' +
        field +
        '=eq.' +
        encodeURIComponent(sourceId) +
        '&select=id,course_assignment_id,configuration_mode',
        { headers }
      ),

      fetch(
        SUPABASE_URL +
        '/rest/v1/course_structured_instance_schedule_items?' +
        field +
        '=eq.' +
        encodeURIComponent(sourceId) +
        '&select=id,configuration_mode,course_structured_instances!inner(course_assignment_id)',
        { headers }
      )
    ]);


  if (!ordinaryResponse.ok) {
    const detail =
      await ordinaryResponse.text();

    throw new Error(
      'Could not check assignment Schedule references (' +
      ordinaryResponse.status +
      '): ' +
      detail
    );
  }


  if (!structuredResponse.ok) {
    const detail =
      await structuredResponse.text();

    throw new Error(
      'Could not check Structured Form Schedule references (' +
      structuredResponse.status +
      '): ' +
      detail
    );
  }


  const ordinary =
    await ordinaryResponse.json();

  const structured =
    await structuredResponse.json();


  const ordinaryChoiceIds =
    (ordinary || [])
      .filter(
        row =>
          row.configuration_mode ===
          'choice'
      )
      .map(row => row.id);


  const structuredChoiceIds =
    (structured || [])
      .filter(
        row =>
          row.configuration_mode ===
          'choice'
      )
      .map(row => row.id);


  const [
    ordinarySelections,
    ordinarySubmissions,
    structuredSelections
  ] =
    await Promise.all([
      ordinaryChoiceIds.length
        ? fetch(
            SUPABASE_URL +
            '/rest/v1/course_assignment_schedule_selections' +
            '?course_assignment_schedule_item_id=in.(' +
            ordinaryChoiceIds
              .map(encodeURIComponent)
              .join(',') +
            ')' +
            '&select=course_assignment_schedule_item_id',
            { headers }
          ).then(response => {
            if (!response.ok) {
              throw new Error(
                'Could not check Schedule choice selections.'
              );
            }

            return response.json();
          })
        : Promise.resolve([]),

      ordinaryChoiceIds.length
        ? fetch(
            SUPABASE_URL +
            '/rest/v1/assignment_submissions' +
            '?course_assignment_schedule_item_id=in.(' +
            ordinaryChoiceIds
              .map(encodeURIComponent)
              .join(',') +
            ')' +
            '&select=course_assignment_schedule_item_id,submitted,drive_file_id,file_name,text',
            { headers }
          ).then(response => {
            if (!response.ok) {
              throw new Error(
                'Could not check scheduled assignment work.'
              );
            }

            return response.json();
          })
        : Promise.resolve([]),

      structuredChoiceIds.length
        ? fetch(
            SUPABASE_URL +
            '/rest/v1/course_structured_instance_schedule_selections' +
            '?course_structured_instance_schedule_item_id=in.(' +
            structuredChoiceIds
              .map(encodeURIComponent)
              .join(',') +
            ')' +
            '&select=course_structured_instance_schedule_item_id',
            { headers }
          ).then(response => {
            if (!response.ok) {
              throw new Error(
                'Could not check Structured Form choices.'
              );
            }

            return response.json();
          })
        : Promise.resolve([])
    ]);


  const selectedOrdinary =
    new Set(
      (ordinarySelections || [])
        .map(
          row =>
            String(
              row.course_assignment_schedule_item_id
            )
        )
    );


  const workedOrdinary =
    new Set(
      (ordinarySubmissions || [])
        .filter(
          row =>
            row.submitted === true ||
            row.drive_file_id ||
            String(
              row.file_name || ''
            ).trim() ||
            String(
              row.text || ''
            )
              .replace(
                /<[^>]*>/g,
                ''
              )
              .trim()
        )
        .map(
          row =>
            String(
              row.course_assignment_schedule_item_id
            )
        )
    );


  const selectedStructured =
    new Set(
      (structuredSelections || [])
        .map(
          row =>
            String(
              row.course_structured_instance_schedule_item_id
            )
        )
    );


  const blocking = [];


  (ordinary || []).forEach(
    row => {
      if (
        row.configuration_mode !==
          'choice' ||
        selectedOrdinary.has(
          String(row.id)
        ) ||
        workedOrdinary.has(
          String(row.id)
        )
      ) {
        blocking.push({
          course_assignment_id:
            row.course_assignment_id
        });
      }
    }
  );


  (structured || []).forEach(
    row => {
      if (
        row.configuration_mode !==
          'choice' ||
        selectedStructured.has(
          String(row.id)
        )
      ) {
        blocking.push({
          course_assignment_id:
            row
              .course_structured_instances
              ?.course_assignment_id
        });
      }
    }
  );


  const seen =
    new Set();


  return blocking.filter(
    row => {
      const id =
        String(
          row.course_assignment_id ||
          ''
        );

      if (
        !id ||
        seen.has(id)
      ) {
        return false;
      }

      seen.add(id);
      return true;
    }
  );
}


async function deleteUnchosenAssignmentScheduleOptions(
  itemType,
  sourceId
) {
  const field =
    itemType === 'reading'
      ? 'week_reading_assignment_id'
      : 'course_event_id';

  const headers = {
    apikey: SUPABASE_KEY,
    Authorization:
      'Bearer ' + SUPABASE_KEY
  };


  const urls = [
    SUPABASE_URL +
      '/rest/v1/course_assignment_schedule_items?' +
      field +
      '=eq.' +
      encodeURIComponent(sourceId) +
      '&configuration_mode=eq.choice',

    SUPABASE_URL +
      '/rest/v1/course_structured_instance_schedule_items?' +
      field +
      '=eq.' +
      encodeURIComponent(sourceId) +
      '&configuration_mode=eq.choice'
  ];


  for (const url of urls) {
    const response =
      await fetch(
        url,
        {
          method: 'DELETE',
          headers
        }
      );

    if (!response.ok) {
      const detail =
        await response.text();

      throw new Error(
        'Could not remove unused Schedule option (' +
        response.status +
        '): ' +
        detail
      );
    }
  }
}

async function moveWeekReadingAssignment(id, courseWeekId, displayOrder) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_week_reading_assignments?id=eq.${id}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({
        course_week_id: courseWeekId,
        display_order: displayOrder
      })
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`Failed to move reading (${response.status}): ${detail}`);
  }

  return response.json();
}


function structuredInFilter(
  values
) {
  return (
    '(' +
    [
      ...new Set(
        (values || [])
          .filter(Boolean)
          .map(String)
      )
    ]
      .map(
        encodeURIComponent
      )
      .join(',') +
    ')'
  );
}


async function readStructuredRows(
  response,
  label
) {
  const body =
    await response.text();


  if (!response.ok) {
    throw new Error(
      label +
      ' failed (' +
      response.status +
      '): ' +
      (
        body ||
        response.statusText
      )
    );
  }


  return body
    ? JSON.parse(body)
    : [];
}


async function fetchInstructorStructuredFormScheduleData(
  assignments
) {
  const structuredAssignments =
    (
      Array.isArray(assignments)
        ? assignments
        : []
    ).filter(
      assignment =>
        assignment
          ?.assignment_types
          ?.assignment_kind ===
        'structured_form'
    );


  if (
    structuredAssignments.length ===
    0
  ) {
    return {
      instances: [],
      groupDeadlines: [],
      scheduleItems: [],
      scheduleSelections: []
    };
  }


  const assignmentIds =
    structuredAssignments
      .map(
        assignment =>
          assignment.id
      )
      .filter(Boolean);


  const assignmentFilter =
    structuredInFilter(
      assignmentIds
    );


  const instanceResponse =
    await fetch(
      SUPABASE_URL +
      '/rest/v1/course_structured_instances' +
      '?course_assignment_id=in.' +
      assignmentFilter +
      '&select=' +
      [
        'id',
        'course_assignment_id',
        'instance_order',
        'open_date',
        'close_date',
        'schedule_open_offset_days',
        'schedule_close_offset_days'
      ].join(',') +
      '&order=instance_order.asc',
      {
        headers: {
          apikey:
            SUPABASE_KEY,

          Authorization:
            'Bearer ' +
            SUPABASE_KEY
        }
      }
    );


  const instances =
    await readStructuredRows(
      instanceResponse,
      'Loading Structured Form instances'
    );


  if (
    !Array.isArray(instances) ||
    instances.length === 0
  ) {
    return {
      instances: [],
      groupDeadlines: [],
      scheduleItems: [],
      scheduleSelections: []
    };
  }


  const instanceFilter =
    structuredInFilter(
      instances.map(
        instance =>
          instance.id
      )
    );


  const headers = {
    apikey:
      SUPABASE_KEY,

    Authorization:
      'Bearer ' +
      SUPABASE_KEY
  };


  const [
    deadlineResponse,
    itemResponse,
    selectionResponse
  ] =
    await Promise.all([
      fetch(
        SUPABASE_URL +
        '/rest/v1/course_structured_instance_group_deadlines' +
        '?course_structured_instance_id=in.' +
        instanceFilter +
        '&select=' +
        [
          'course_structured_instance_id',
          'group_number',
          'open_date',
          'close_date'
        ].join(','),
        { headers }
      ),

      fetch(
        SUPABASE_URL +
        '/rest/v1/course_structured_instance_schedule_items' +
        '?course_structured_instance_id=in.' +
        instanceFilter +
        '&select=*' +
        '&order=display_order.asc,created_at.asc',
        { headers }
      ),

      fetch(
        SUPABASE_URL +
        '/rest/v1/course_structured_instance_schedule_selections' +
        '?course_structured_instance_id=in.' +
        instanceFilter +
        '&select=' +
        [
          'course_structured_instance_id',
          'course_structured_instance_schedule_item_id',
          'enrolment_id',
          'group_number'
        ].join(','),
        { headers }
      )
    ]);


  const [
    groupDeadlines,
    scheduleItems,
    scheduleSelections
  ] =
    await Promise.all([
      readStructuredRows(
        deadlineResponse,
        'Loading Structured Form group deadlines'
      ),

      readStructuredRows(
        itemResponse,
        'Loading Structured Form Schedule items'
      ),

      readStructuredRows(
        selectionResponse,
        'Loading Structured Form Schedule selections'
      )
    ]);


  return {
    instances:
      Array.isArray(instances)
        ? instances
        : [],

    groupDeadlines:
      Array.isArray(groupDeadlines)
        ? groupDeadlines
        : [],

    scheduleItems:
      Array.isArray(scheduleItems)
        ? scheduleItems
        : [],

    scheduleSelections:
      Array.isArray(scheduleSelections)
        ? scheduleSelections
        : []
  };
}


function expandInstructorStructuredFormOccurrences({
  assignments,
  assignmentTypeSettings,
  structuredData,
  scheduleContext
}) {
  const structuredAssignments =
    (
      Array.isArray(assignments)
        ? assignments
        : []
    ).filter(
      assignment =>
        assignment
          ?.assignment_types
          ?.assignment_kind ===
        'structured_form'
    );


  if (
    structuredAssignments.length ===
    0
  ) {
    return [];
  }


  const settingsByType =
    new Map(
      (
        Array.isArray(
          assignmentTypeSettings
        )
          ? assignmentTypeSettings
          : []
      ).map(
        setting => [
          String(
            setting.assignment_type_id
          ),
          setting
        ]
      )
    );


  const assignmentById =
    new Map(
      structuredAssignments.map(
        assignment => [
          String(
            assignment.id
          ),
          assignment
        ]
      )
    );


  const instances =
    Array.isArray(
      structuredData?.instances
    )
      ? structuredData.instances
      : [];


  const scheduleItems =
    Array.isArray(
      structuredData?.scheduleItems
    )
      ? structuredData.scheduleItems
      : [];


  const scheduleSelections =
    Array.isArray(
      structuredData?.scheduleSelections
    )
      ? structuredData.scheduleSelections
      : [];


  const groupDeadlines =
    Array.isArray(
      structuredData?.groupDeadlines
    )
      ? structuredData.groupDeadlines
      : [];


  const result = [];


  function pushOccurrence({
    assignment,
    instance,
    openDate,
    dueDate,
    scheduleItem = null,
    scheduleSource = null,
    groupNumbers = [],
    keySuffix
  }) {
    result.push({
      ...assignment,

      structured_form_checkpoint:
        true,

      structured_form_instance_id:
        instance.id,

      structured_form_instance_order:
        instance.instance_order,

      schedule_group_numbers:
        groupNumbers,

      instructor_assignment_occurrence_key:
        [
          'structured',
          assignment.id,
          instance.id,
          keySuffix
        ]
          .filter(
            value =>
              value !== null &&
              value !== undefined &&
              value !== ''
          )
          .join('|'),

      schedule_occurrence:
        Boolean(
          scheduleItem
        ),

      course_structured_instance_schedule_item_id:
        scheduleItem?.id ||
        null,

      schedule_item_type:
        scheduleItem
          ?.item_type ||
        null,

      schedule_source_title:
        scheduleSource
          ?.title ||
        '',

      schedule_source_meta:
        scheduleSource
          ?.meta ||
        '',

      schedule_anchor_date:
        scheduleSource
          ?.anchorDate ||
        null,

      effective_open_date:
        openDate ||
        null,

      effective_base_due_date:
        dueDate ||
        null,

      effective_due_date:
        dueDate ||
        null
    });
  }


  instances.forEach(
    instance => {
      const assignment =
        assignmentById.get(
          String(
            instance.course_assignment_id
          )
        );


      if (!assignment) {
        return;
      }


      const setting =
        settingsByType.get(
          String(
            assignment.assignment_type_id
          )
        ) || {};


      const scheduleEnabled =
        setting.schedule_enabled ===
        true;


      const choiceEnabled =
        scheduleEnabled &&
        setting
          .schedule_choice_enabled ===
        true;


      if (scheduleEnabled) {
        const mode =
          choiceEnabled
            ? 'choice'
            : 'single';


        const instanceItems =
          scheduleItems
            .filter(
              item =>
                String(
                  item.course_structured_instance_id
                ) ===
                  String(
                    instance.id
                  ) &&

                item.configuration_mode ===
                  mode
            )
            .sort(
              (a, b) =>
                (
                  Number(
                    a.display_order
                  ) || 0
                ) -
                (
                  Number(
                    b.display_order
                  ) || 0
                ) ||

                String(
                  a.id
                ).localeCompare(
                  String(
                    b.id
                  )
                )
            );


        const selectedItemIds =
          choiceEnabled
            ? new Set(
                scheduleSelections
                  .filter(
                    selection =>
                      String(
                        selection
                          .course_structured_instance_id
                      ) ===
                      String(
                        instance.id
                      )
                  )
                  .map(
                    selection =>
                      String(
                        selection
                          .course_structured_instance_schedule_item_id
                      )
                  )
                  .filter(Boolean)
              )
            : null;


        const displayedItems =
          choiceEnabled
            ? instanceItems.filter(
                item =>
                  selectedItemIds.has(
                    String(
                      item.id
                    )
                  )
              )
            : instanceItems;


        /*
         * Choice-mode options are only possibilities.
         * Until somebody/group actually selects one,
         * there is no real occurrence for the instructor
         * Teaching Schedule.
         */
        displayedItems.forEach(
          item => {
            const selections =
              choiceEnabled
                ? scheduleSelections.filter(
                    selection =>
                      String(
                        selection
                          .course_structured_instance_id
                      ) ===
                        String(
                          instance.id
                        ) &&

                      String(
                        selection
                          .course_structured_instance_schedule_item_id
                      ) ===
                        String(
                          item.id
                        )
                  )
                : [];


            const groupNumbers =
              Array.from(
                new Set(
                  selections
                    .map(
                      selection =>
                        selection
                          .group_number
                    )
                    .filter(
                      value =>
                        value !== null &&
                        value !== undefined &&
                        value !== ''
                    )
                    .map(Number)
                    .filter(
                      Number.isFinite
                    )
                )
              )
                .sort(
                  (a, b) =>
                    a - b
                );


            const dates =
              getScheduleItemDates(
                item,

                {
                  schedule_open_offset_days:
                    instance
                      .schedule_open_offset_days,

                  schedule_due_offset_days:
                    instance
                      .schedule_close_offset_days
                },

                scheduleContext,

                0
              );


            pushOccurrence({
              assignment,
              instance,

              openDate:
                dates.openDate,

              dueDate:
                dates.baseDueDate,

              scheduleItem:
                item,

              scheduleSource:
                dates,

              groupNumbers,

              keySuffix:
                item.id
            });
          }
        );


        return;
      }


      /*
       * No Event/Reading schedule anchoring.
       *
       * If group-specific Structured Form deadlines
       * are enabled, show one calendar occurrence
       * for each distinct effective date pair, with
       * the relevant groups attached.
       */
      const usesGroupDeadlines =
        setting
          .group_deadlines_enabled ===
          true &&

        assignment.groups_enabled ===
          true &&

        Number(
          assignment.group_count
        ) > 0;


      if (usesGroupDeadlines) {
        const rowsByDates =
          new Map();


        for (
          let groupNumber = 1;
          groupNumber <=
            Number(
              assignment.group_count
            );
          groupNumber += 1
        ) {
          const override =
            groupDeadlines.find(
              row =>
                String(
                  row.course_structured_instance_id
                ) ===
                  String(
                    instance.id
                  ) &&

                Number(
                  row.group_number
                ) ===
                  groupNumber
            ) || null;


          const openDate =
            override?.open_date ||
            instance.open_date ||
            null;


          const dueDate =
            override?.close_date ||
            instance.close_date ||
            null;


          const key =
            String(
              openDate || ''
            ) +
            '|' +
            String(
              dueDate || ''
            );


          if (
            !rowsByDates.has(
              key
            )
          ) {
            rowsByDates.set(
              key,
              {
                openDate,
                dueDate,
                groupNumbers: []
              }
            );
          }


          rowsByDates
            .get(key)
            .groupNumbers
            .push(
              groupNumber
            );
        }


        Array.from(
          rowsByDates.entries()
        ).forEach(
          ([key, row]) => {
            pushOccurrence({
              assignment,
              instance,

              openDate:
                row.openDate,

              dueDate:
                row.dueDate,

              groupNumbers:
                row.groupNumbers,

              keySuffix:
                'groups|' +
                key
            });
          }
        );


        return;
      }


      /*
       * Ordinary Structured Form instance:
       * one instructor-visible occurrence.
       */
      pushOccurrence({
        assignment,
        instance,

        openDate:
          instance.open_date ||
          null,

        dueDate:
          instance.close_date ||
          null,

        groupNumbers:
          [],

        keySuffix:
          'instance'
      });
    }
  );


  return result;
}


function formatDate(dateStr) {
  if (!dateStr) return '';

  const d = new Date(dateStr);

  return d.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

function getCalendarWeekStart(dateStr) {
  if (!dateStr) return '';

  const parts = String(dateStr)
    .slice(0, 10)
    .split('-')
    .map(Number);

  if (
    parts.length !== 3 ||
    !parts.every(Number.isFinite)
  ) {
    return '';
  }

  const date = new Date(
    parts[0],
    parts[1] - 1,
    parts[2]
  );

  const dayOfWeek = date.getDay();

  date.setDate(
    date.getDate() -
    (dayOfWeek === 0 ? 6 : dayOfWeek - 1)
  );

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ].join('-');
}

function getAssignmentDisplayName(
  assignment
) {
  return (
    String(
      assignment
        ?.assignment_display_name ||
      ''
    ).trim() ||
    assignment
      ?.assignment_types
      ?.name ||
    'Assignment'
  );
}

function getAssignmentScheduleDate(
  assignment
) {
  const requiresSubmission =
    assignment
      ?.assignment_types
      ?.requires_submission ??
    true;

  if (!requiresSubmission) {
    return (
      assignment.occurs_on ||
      assignment
        .effective_due_date ||
      assignment.due_date ||
      null
    );
  }

  return (
    assignment
      .effective_due_date ||
    assignment.due_date ||
    null
  );
}

function getAssignmentScheduleLabel(
  assignment,
  baseAssignments
) {
  const sameTypeCount =
    new Set(
      (baseAssignments || [])
        .filter(
          item =>
            item
              .assignment_type_id ===
            assignment
              .assignment_type_id
        )
        .map(
          item =>
            String(item.id)
        )
    ).size;

  const instanceSuffix =
    sameTypeCount > 1
      ? ` ${assignment.instance_number}`
      : '';

  const structuredInstanceSuffix =
    assignment
      .structured_form_checkpoint
      ? ` — Part ${
          assignment
            .structured_form_instance_order
        }`
      : '';


  const sourceSuffix =
    assignment
      .schedule_source_title
      ? ` — ${assignment.schedule_source_title}`
      : '';

  const groupSuffix =
    Array.isArray(
      assignment.schedule_group_names
    ) &&
    assignment.schedule_group_names.length > 0
      ? ` — ${assignment.schedule_group_names.join(', ')}`
      : '';

  return (
    getAssignmentDisplayName(
      assignment
    ) +
    instanceSuffix +
    structuredInstanceSuffix +
    sourceSuffix +
    groupSuffix
  );
}

function applyAssignmentDisplayNames(
  assignments,
  settings
) {
  const names =
    new Map(
      (settings || []).map(
        setting => [
          String(
            setting
              .assignment_type_id
          ),
          String(
            setting.display_name ||
            ''
          ).trim()
        ]
      )
    );

  return (assignments || []).map(
    assignment => ({
      ...assignment,
      assignment_display_name:
        names.get(
          String(
            assignment
              .assignment_type_id
          )
        ) ||
        assignment
          ?.assignment_types
          ?.name ||
        'Assignment'
    })
  );
}

export default function CourseSchedule({ course }) {
  const [weeks, setWeeks] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [courseAssignments, setCourseAssignments] = useState([]);
  const [calendarAssignments, setCalendarAssignments] = useState([]);
  const [
    calendarTypeSettings,
    setCalendarTypeSettings
  ] = useState({});

  const [
    calendarScheduleContext,
    setCalendarScheduleContext
  ] = useState({
    scheduleItems: [],
    scheduleReadings: [],
    scheduleEvents: []
  });
  const [weekContent, setWeekContent] = useState({});
  const [courseReadings, setCourseReadings] = useState([]);
  const [weekReadingAssignments, setWeekReadingAssignments] = useState([]);
  const [courseEvents, setCourseEvents] = useState([]);
  const [newEvents, setNewEvents] = useState({});
  const [editingEventId, setEditingEventId] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [movingEventId, setMovingEventId] = useState(null);
  const [movingReadingId, setMovingReadingId] = useState(null);
  const [moveWeekTargets, setMoveWeekTargets] = useState({});
  const [allCourses, setAllCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedWeekId, setExpandedWeekId] = useState(null);
  const [editDescriptions, setEditDescriptions] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newReading, setNewReading] = useState({});
  const [editingReadingId, setEditingReadingId] = useState(null);
  const [editingReading, setEditingReading] = useState(null);
  const [uploading, setUploading] = useState({});
  const [importCourseId, setImportCourseId] = useState({});
  const [importCourseReadings, setImportCourseReadings] = useState({});
  const [importReadingId, setImportReadingId] = useState({});
  const [loadingImport, setLoadingImport] = useState({});

  useEffect(() => {
    async function loadAll() {
      const [
        w,
        h,
        m,
        ca,
        assignmentSettings,
        scheduleContext,
        assignmentSelections,
        assignmentGroupLabels,
        wc,
        cr,
        wra,
        ce,
        ac
      ] = await Promise.all([
        fetchWeeks(course.id),
        fetchHolidays(course.instructor_id),
        fetchMeetings(course.id),
        fetchCourseAssignments(course.id),
        fetchAssignmentTypeSettings(course.id),
        loadStudentAssignmentScheduleContext({
          courseIds: [course.id],
          enrolments: [],
          includeChoiceState: false
        }),
        fetchAssignmentScheduleSelections(
          course.id
        ),
        fetchAssignmentGroupLabelsForCourse(
          course.id
        ),
        fetchWeekContent(course.id),
        fetchCourseReadings(course.id),
        fetchWeekReadingAssignments(course.id),
        fetchCourseEvents(course.id),
        fetchAllCourses()
      ]);

      const namedAssignments =
        applyAssignmentDisplayNames(
          Array.isArray(ca)
            ? ca
            : [],
          Array.isArray(
            assignmentSettings
          )
            ? assignmentSettings
            : []
        );

      /*
       * Structured Form assignments have their own
       * instance Schedule tables. Do not send their
       * parent assignment through the ordinary
       * Schedule occurrence expander.
       */
      const ordinaryAssignments =
        namedAssignments.filter(
          assignment =>
            assignment
              ?.assignment_types
              ?.assignment_kind !==
            'structured_form'
        );


      const expandedAssignments =
        expandInstructorAssignmentScheduleOccurrences({
          assignments:
            ordinaryAssignments,

          assignmentTypeSettings:
            Array.isArray(
              assignmentSettings
            )
              ? assignmentSettings
              : [],

          scheduleContext,

          scheduleSelections:
            Array.isArray(
              assignmentSelections
            )
              ? assignmentSelections
              : []
        });


      const structuredData =
        await fetchInstructorStructuredFormScheduleData(
          namedAssignments
        );


      const structuredOccurrences =
        expandInstructorStructuredFormOccurrences({
          assignments:
            namedAssignments,

          assignmentTypeSettings:
            Array.isArray(
              assignmentSettings
            )
              ? assignmentSettings
              : [],

          structuredData,

          scheduleContext
        });


      const calendarRows = [
        ...expandedAssignments,
        ...structuredOccurrences
      ];

      setWeeks(w);
      setHolidays(h);
      setMeetings(m);
      setCourseAssignments(
        namedAssignments
      );

      const pickerSettings = {};

      (
        Array.isArray(
          assignmentSettings
        )
          ? assignmentSettings
          : []
      ).forEach(
        setting => {
          const id =
            String(
              setting
                ?.assignment_type_id ||
              ''
            );

          if (id) {
            pickerSettings[id] =
              setting;
          }
        }
      );

      setCalendarTypeSettings(
        pickerSettings
      );

      setCalendarScheduleContext(
        scheduleContext || {
          scheduleItems: [],
          scheduleReadings: [],
          scheduleEvents: []
        }
      );

      setCalendarAssignments(
        calendarRows.map(
          assignment => {
            const groupNames =
              Array.from(
                new Set(
                  (
                    assignment.schedule_group_numbers ||
                    []
                  )
                    .map(
                      groupNumber =>
                        getAssignmentGroupDisplayName(
                          assignmentGroupLabels,
                          assignment.id,
                          groupNumber
                        )
                    )
                    .filter(Boolean)
                )
              );

            return {
              ...assignment,

              schedule_group_names:
                groupNames
            };
          }
        )
      );

      const contentMap = {};
      wc.forEach(c => { contentMap[c.course_week_id] = c; });
      setWeekContent(contentMap);

      setCourseReadings(cr);
      setWeekReadingAssignments(wra);
      setCourseEvents(Array.isArray(ce) ? ce : []);
      setAllCourses(ac.filter(c => c.id !== course.id));
      setLoading(false);
    }
    loadAll();
    }, [
    course.id,
    course.instructor_id
  ]);

  async function handleLoadImportCourse(weekId, sourceCourseId) {
    setImportCourseId(prev => ({ ...prev, [weekId]: sourceCourseId }));
    if (!sourceCourseId) {
      setImportCourseReadings(prev => ({ ...prev, [weekId]: [] }));
      return;
    }
    setLoadingImport(prev => ({ ...prev, [weekId]: true }));
    const readings = await fetchReadingsForCourse(sourceCourseId);
    setImportCourseReadings(prev => ({ ...prev, [weekId]: readings }));
    setLoadingImport(prev => ({ ...prev, [weekId]: false }));
  }

  async function handleImportReading(weekId, readingId) {
    if (!readingId) return;
    const weekAssignments = weekReadingAssignments.filter(a => a.course_week_id === weekId);
    const already = weekAssignments.find(a => a.course_reading_id === readingId);
    if (already) { setError('That reading is already assigned to this week.'); return; }

    const sourceReading = importCourseReadings[weekId]?.find(r => r.id === readingId);
    if (!sourceReading) return;

    setSaving(true);
    setError('');
    try {
      let targetReadingId = courseReadings.find(r => r.drive_file_id === sourceReading.drive_file_id)?.id;
      if (!targetReadingId) {
        const result = await saveCourseReading({
          course_id: course.id,
          title: sourceReading.title,
          author: sourceReading.author || null,
          drive_file_id: sourceReading.drive_file_id,
          drive_file_name: sourceReading.drive_file_name
        });
        targetReadingId = result[0].id;
        setCourseReadings(prev => [...prev, result[0]]);
      }

      const result = await assignReadingToWeek(weekId, targetReadingId, weekAssignments.length + 1);
      setWeekReadingAssignments(prev => [...prev, result[0]]);
      setImportReadingId(prev => ({ ...prev, [weekId]: '' }));
      setSuccess('Reading added from other course.');
    } catch (err) {
      setError('Failed to import reading.');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleWeek(weekId, field, currentValue) {
    const newValue = !currentValue;
    await fetch(
      `${SUPABASE_URL}/rest/v1/course_weeks?id=eq.${weekId}`,
      {
        method: 'PATCH',
        headers: {
          'apikey': SUPABASE_KEY,
          'Authorization': 'Bearer ' + SUPABASE_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [field]: newValue })
      }
    );
    setWeeks(prev => prev.map(w => w.id === weekId ? { ...w, [field]: newValue } : w));
  }
  function getHolidayForWeek(week) {
  return Array.isArray(holidays)
    ? holidays.find(h => h.date === week.class_date)
    : null;
}

  function isFirstActualClassOfWeek(week) {
    const weekStart =
      getCalendarWeekStart(week.class_date);

    const sameWeek = weeks
      .filter(
        candidate =>
          getCalendarWeekStart(
            candidate.class_date
          ) === weekStart
      )
      .sort(
        (a, b) =>
          String(a.class_date).localeCompare(
            String(b.class_date)
          )
      );

    const firstActualClass =
      sameWeek.find(
        candidate =>
          !getHolidayForWeek(candidate)
      ) || sameWeek[0];

    return firstActualClass?.id === week.id;
  }

  function getMeetingsForWeek(week) {
    const classDate = new Date(week.class_date);
    const dayOfWeek = classDate.getDay();
    const monday = new Date(classDate);
    monday.setDate(classDate.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return meetings.filter(m => {
      const d = new Date(m.meeting_date);
      return d >= monday && d <= sunday;
    });
  }

  function getAssignmentsDueInWeek(week) {
    const weekStart =
      getCalendarWeekStart(
        week.class_date
      );

    return calendarAssignments
      .filter(assignment => {
        const date =
          getAssignmentScheduleDate(
            assignment
          );

        return (
          date &&
          getCalendarWeekStart(
            date
          ) ===
            weekStart
        );
      })
      .sort((a, b) => {
        const dateCompare =
          String(
            getAssignmentScheduleDate(a) ||
            ''
          ).localeCompare(
            String(
              getAssignmentScheduleDate(b) ||
              ''
            )
          );

        if (dateCompare !== 0) {
          return dateCompare;
        }

        const labelCompare =
          String(
            getAssignmentScheduleLabel(
              a,
              courseAssignments
            ) ||
            ''
          ).localeCompare(
            String(
              getAssignmentScheduleLabel(
                b,
                courseAssignments
              ) ||
              ''
            ),
            undefined,
            {
              numeric: true,
              sensitivity: 'base'
            }
          );

        if (labelCompare !== 0) {
          return labelCompare;
        }

        return String(
          a.instructor_assignment_occurrence_key ||
          a.id ||
          ''
        ).localeCompare(
          String(
            b.instructor_assignment_occurrence_key ||
            b.id ||
            ''
          )
        );
      });
  }

  function getWeekReadings(weekId) {
    return weekReadingAssignments
      .filter(a => a.course_week_id === weekId)
      .sort((a, b) => a.display_order - b.display_order);
  }

  function getWeekEvents(weekId) {
    return courseEvents
      .filter(event => event.course_week_id === weekId)
      .sort((a, b) => {
        const dateCompare = String(a.event_date || '').localeCompare(
          String(b.event_date || '')
        );

        if (dateCompare !== 0) return dateCompare;

        return String(a.event_time || '').localeCompare(
          String(b.event_time || '')
        );
      });
  }

  function handleExpand(weekId) {
    if (expandedWeekId === weekId) {
      setExpandedWeekId(null);
      return;
    }
    setExpandedWeekId(weekId);
    setEditDescriptions(prev => ({
      ...prev,
      [weekId]: weekContent[weekId]?.description || ''
    }));
    setError('');
    setSuccess('');
  }

  async function handleSaveDescription(weekId) {
    const description = editDescriptions[weekId] || '';

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (!hasMeaningfulRichText(description)) {
        await deleteWeekContent(weekId);

        setWeekContent(prev => {
          const next = { ...prev };
          delete next[weekId];
          return next;
        });

        setEditDescriptions(prev => ({
          ...prev,
          [weekId]: ''
        }));

        setSuccess('Description removed.');
        return;
      }

      const saved = await saveWeekContent(
        weekId,
        description
      );

      setWeekContent(prev => ({
        ...prev,
        [weekId]: saved
      }));

      setSuccess('Description saved.');
    } catch (err) {
      console.error('Week description save failed:', err);
      setError(err?.message || 'Failed to save description.');
    } finally {
      setSaving(false);
    }
  }

  async function handleAddEvent(week) {
    const draft = newEvents[week.id] || {
      title: '',
      event_date: '',
      event_time: '',
      location: ''
    };

    if (!draft.title?.trim()) {
      setError('Enter an event title.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const result = await createCourseEvent({
        course_week_id: week.id,
        title: draft.title.trim(),
        event_date: draft.event_date || null,
        event_time: draft.event_time || null,
        location: draft.location?.trim() || null
      });

      const created = result?.[0];

      if (!created?.id) {
        throw new Error('Event record was not created.');
      }

      setCourseEvents(prev =>
        sortCourseEvents([...prev, created])
      );

      setNewEvents(prev => ({
        ...prev,
        [week.id]: {
          title: '',
          event_date: '',
          event_time: '',
          location: ''
        }
      }));

      setSuccess('Event added.');
    } catch (err) {
      console.error('Event creation failed:', err);
      setError(err?.message || 'Failed to add event.');
    } finally {
      setSaving(false);
    }
  }

  function startEditingEvent(event) {
    setEditingEventId(event.id);
    setEditingEvent({
      title: event.title || '',
      event_date: event.event_date || '',
      event_time: event.event_time
        ? String(event.event_time).slice(0, 5)
        : '',
      location: event.location || ''
    });
    setError('');
    setSuccess('');
  }

  function cancelEditingEvent() {
    setEditingEventId(null);
    setEditingEvent(null);
  }

  async function handleSaveEvent(eventId) {
    if (!editingEvent?.title?.trim()) {
      setError('Enter an event title.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const result = await updateCourseEvent(eventId, {
        title: editingEvent.title.trim(),
        event_date: editingEvent.event_date || null,
        event_time: editingEvent.event_time || null,
        location: editingEvent.location?.trim() || null
      });

      const updated = result?.[0];

      if (!updated?.id) {
        throw new Error('Event update was not returned.');
      }

      setCourseEvents(prev =>
        sortCourseEvents(
          prev.map(event =>
            event.id === eventId ? updated : event
          )
        )
      );

      cancelEditingEvent();
      setSuccess('Event saved.');
    } catch (err) {
      console.error('Event update failed:', err);
      setError(err?.message || 'Failed to save event.');
    } finally {
      setSaving(false);
    }
  }

  function assignmentReferenceLabels(references) {
    return (references || []).map(reference => {
      const assignment = courseAssignments.find(
        item => item.id === reference.course_assignment_id
      );

      if (!assignment) {
        return 'an assignment';
      }

      return getAssignmentScheduleLabel(
        assignment,
        courseAssignments
      );
    });
  }

  function beginMoveEvent(event) {
    setMovingEventId(event.id);
    setMovingReadingId(null);
    setMoveWeekTargets(prev => ({
      ...prev,
      [`event-${event.id}`]: event.course_week_id
    }));
    setError('');
    setSuccess('');
  }

  function beginEditingReading(assignment) {
    const reading =
      assignment?.course_readings ||
      courseReadings.find(
        item => item.id === assignment?.course_reading_id
      );

    if (!reading?.id) {
      setError('Could not find this reading.');
      return;
    }

    setEditingReadingId(assignment.id);
    setEditingReading({
      readingId: reading.id,
      title: reading.title || '',
      author: reading.author || ''
    });

    setMovingReadingId(null);
    setError('');
    setSuccess('');
  }

  function cancelEditingReading() {
    setEditingReadingId(null);
    setEditingReading(null);
  }

  async function handleSaveReading() {
    if (
      !editingReading?.readingId ||
      !editingReading.title?.trim()
    ) {
      setError('Reading title is required.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const updated = await updateCourseReading(
        editingReading.readingId,
        {
          title: editingReading.title.trim(),
          author: editingReading.author?.trim() || null
        }
      );

      setCourseReadings(previous =>
        previous.map(reading =>
          reading.id === updated.id
            ? { ...reading, ...updated }
            : reading
        )
      );

      setWeekReadingAssignments(previous =>
        previous.map(row =>
          row.course_reading_id === updated.id
            ? {
                ...row,
                course_readings: {
                  ...(row.course_readings || {}),
                  ...updated
                }
              }
            : row
        )
      );

      cancelEditingReading();
      setSuccess('Reading details saved.');
    } catch (err) {
      console.error('Reading update failed:', err);

      setError(
        err?.message ||
          'Failed to save reading details.'
      );
    } finally {
      setSaving(false);
    }
  }

  function beginMoveReading(assignment) {
    setMovingReadingId(assignment.id);
    setMovingEventId(null);
    setMoveWeekTargets(prev => ({
      ...prev,
      [`reading-${assignment.id}`]: assignment.course_week_id
    }));
    setError('');
    setSuccess('');
  }

  function cancelMoveEvent() {
    setMovingEventId(null);
  }

  function cancelMoveReading() {
    setMovingReadingId(null);
  }

  async function handleMoveEvent(event) {
    const targetWeekId = moveWeekTargets[`event-${event.id}`];

    if (!targetWeekId || targetWeekId === event.course_week_id) {
      cancelMoveEvent();
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const result = await updateCourseEvent(event.id, {
        course_week_id: targetWeekId
      });

      const updated = result?.[0];

      if (!updated?.id) {
        throw new Error('Event move was not returned.');
      }

      setCourseEvents(prev =>
        sortCourseEvents(
          prev.map(item =>
            item.id === event.id ? { ...item, ...updated } : item
          )
        )
      );

      cancelMoveEvent();
      setSuccess('Event moved. Any relative assignment dates will follow its new schedule anchor.');
    } catch (err) {
      console.error('Event move failed:', err);
      setError(err?.message || 'Failed to move event.');
    } finally {
      setSaving(false);
    }
  }

  async function handleMoveReading(assignment) {
    const targetWeekId = moveWeekTargets[`reading-${assignment.id}`];

    if (!targetWeekId || targetWeekId === assignment.course_week_id) {
      cancelMoveReading();
      return;
    }

    const destinationAssignments = weekReadingAssignments.filter(
      item => item.course_week_id === targetWeekId
    );

    const duplicate = destinationAssignments.some(
      item =>
        item.id !== assignment.id &&
        item.course_reading_id === assignment.course_reading_id
    );

    if (duplicate) {
      setError('That reading is already assigned to the destination week.');
      return;
    }

    const nextDisplayOrder = destinationAssignments.reduce(
      (max, item) => Math.max(max, Number(item.display_order) || 0),
      0
    ) + 1;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const result = await moveWeekReadingAssignment(
        assignment.id,
        targetWeekId,
        nextDisplayOrder
      );

      const updated = result?.[0];

      if (!updated?.id) {
        throw new Error('Reading move was not returned.');
      }

      setWeekReadingAssignments(prev =>
        prev.map(item =>
          item.id === assignment.id
            ? {
                ...item,
                ...updated,
                course_readings: item.course_readings
              }
            : item
        )
      );

      cancelMoveReading();
      setSuccess('Reading moved. Any relative assignment dates will follow its new schedule anchor.');
    } catch (err) {
      console.error('Reading move failed:', err);
      setError(err?.message || 'Failed to move reading.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteEvent(event) {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const references = await fetchAssignmentScheduleReferences(
        'event',
        event.id
      );

      if (references.length > 0) {
        const labels = assignmentReferenceLabels(references);
        setError(
          `Cannot delete "${event.title}" because it is used by ${labels.join(', ')}. Remove those Schedule links first.`
        );
        return;
      }

      if (!window.confirm(`Delete "${event.title}" from the course schedule?`)) {
        return;
      }

      await deleteUnchosenAssignmentScheduleOptions(
        'event',
        event.id
      );

      await deleteCourseEvent(event.id);

      setCourseEvents(prev =>
        prev.filter(item => item.id !== event.id)
      );

      if (editingEventId === event.id) {
        cancelEditingEvent();
      }

      if (movingEventId === event.id) {
        cancelMoveEvent();
      }

      setSuccess('Event deleted.');
    } catch (err) {
      console.error('Event deletion failed:', err);
      setError(err?.message || 'Failed to delete event.');
    } finally {
      setSaving(false);
    }
  }

async function handleUploadReading(weekId, file) {
  if (!file) return;

  const nr = newReading[weekId] || {};

  if (!nr.title?.trim()) {
    setError('Enter a title before uploading.');
    return;
  }

  const uploadFileName = makeReadingFileName(
    nr.author,
    nr.title,
    file.name
  );

  setUploading(prev => ({ ...prev, [weekId]: true }));
  setError('');
  setSuccess('');

  try {
    // Read the selected file
    const base64Data = await new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = e => {
        try {
          resolve(e.target.result.split(',')[1]);
        } catch (err) {
          reject(err);
        }
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    // FIRST create the reading record in Supabase.
    // This gives Apps Script the exact reading ID it needs to update.
    const readingResult = await saveCourseReading({
      course_id: course.id,
      title: nr.title.trim(),
      author: nr.author?.trim() || null,
      drive_file_name: uploadFileName,
      drive_file_id: null
    });

    const newCourseReading = readingResult[0];

    if (!newCourseReading?.id) {
      throw new Error('Could not create reading record.');
    }

    // THEN upload the file to Drive, giving Apps Script
    // the reading ID that it must update.
    const folderResponse = await fetch(
  `${SUPABASE_URL}/rest/v1/courses?id=eq.${course.id}&select=readings_folder_id`,
  {
    headers: {
      'apikey': SUPABASE_KEY,
      'Authorization': 'Bearer ' + SUPABASE_KEY
    }
  }
);

const folderData = await folderResponse.json();
let readingsFolderId = folderData[0]?.readings_folder_id;

if (!readingsFolderId) {
  // No course-specific folder yet: ask Apps Script to create one
  await fetch(API_URL, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'createCourseReadingsFolder',
      courseName: course.course_titles?.name || course.name || `Course ${course.id}`,
      courseId: course.id
    })
  });

  // Apps Script writes the new folder ID back to Supabase.
  // Wait for that to happen.
  for (let i = 0; i < 10; i++) {
    await new Promise(resolve => setTimeout(resolve, 1000));

    const checkResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/courses?id=eq.${course.id}&select=readings_folder_id`,
      {
        headers: {
          'apikey': SUPABASE_KEY,
          'Authorization': 'Bearer ' + SUPABASE_KEY
        }
      }
    );

    const checkData = await checkResponse.json();

    if (checkData[0]?.readings_folder_id) {
      readingsFolderId = checkData[0].readings_folder_id;
      break;
    }
  }

  if (!readingsFolderId) {
    throw new Error('Could not create the readings Drive folder for this course.');
  }
}
    await fetch(API_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'uploadReading',
        courseId: course.id,
        readingId: newCourseReading.id,
        readingsFolderId,
        fileName: uploadFileName,
        base64Data,
        mimeType: file.type || 'application/octet-stream'
      })
    });

    // Because the request is no-cors, we cannot read the Apps Script
    // response directly. Poll Supabase until Apps Script has written
    // the Google Drive file ID back to this reading.
    let fileId = null;

    for (let i = 0; i < 10; i++) {
      await new Promise(resolve => setTimeout(resolve, 2000));

      const checkResponse = await fetch(
        `${SUPABASE_URL}/rest/v1/course_readings?id=eq.${newCourseReading.id}&select=drive_file_id`,
        {
          headers: {
            'apikey': SUPABASE_KEY,
            'Authorization': 'Bearer ' + SUPABASE_KEY
          }
        }
      );

      const checkData = await checkResponse.json();

      if (checkData[0]?.drive_file_id) {
        fileId = checkData[0].drive_file_id;
        break;
      }
    }

    if (!fileId) {
      throw new Error(
        'The file upload was not confirmed. Check the Apps Script execution log.'
      );
    }

    // Add the confirmed Drive ID to the local reading object
    newCourseReading.drive_file_id = fileId;

    setCourseReadings(prev => [
      ...prev,
      newCourseReading
    ]);

    // Finally assign the reading to this week
    const weekAssignments = weekReadingAssignments.filter(
      a => a.course_week_id === weekId
    );

    const assignResult = await assignReadingToWeek(
      weekId,
      newCourseReading.id,
      weekAssignments.length + 1
    );

    setWeekReadingAssignments(prev => [
      ...prev,
      {
        ...assignResult[0],
        course_readings: newCourseReading
      }
    ]);

    setNewReading(prev => ({
      ...prev,
      [weekId]: {
        title: '',
        author: ''
      }
    }));

    setSuccess('Reading uploaded and VERIFIED.');

  } catch (err) {
    console.error('Reading upload failed:', err);

    setError(
      err?.message || 'Failed to upload reading.'
    );

  } finally {
    setUploading(prev => ({
      ...prev,
      [weekId]: false
    }));
  }
}

  async function handleRemoveReading(assignmentId) {
    const assignment = weekReadingAssignments.find(
      item => item.id === assignmentId
    );

    if (!assignment) {
      setError('Could not find that reading assignment.');
      return;
    }

    const reading = assignment.course_readings;
    const label = reading?.title || 'this reading';

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const references = await fetchAssignmentScheduleReferences(
        'reading',
        assignmentId
      );

      if (references.length > 0) {
        const labels = assignmentReferenceLabels(references);
        setError(
          `Cannot delete "${label}" from this week because it is used by ${labels.join(', ')}. Remove those Schedule links first.`
        );
        return;
      }

      if (!window.confirm(
        `Delete "${label}" from this week? ` +
        'If this is the last place the reading is used, its Drive file will also be deleted.'
      )) return;

      await deleteUnchosenAssignmentScheduleOptions(
        'reading',
        assignmentId
      );

      await requestReadingDeletion(assignmentId);

      let stillExists = true;

      for (let i = 0; i < 10; i++) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        stillExists = await readingAssignmentStillExists(assignmentId);

        if (!stillExists) break;
      }

      if (stillExists) {
        throw new Error(
          'The reading deletion was not confirmed. Check the Apps Script execution log.'
        );
      }

      const [freshReadings, freshAssignments] = await Promise.all([
        fetchCourseReadings(course.id),
        fetchWeekReadingAssignments(course.id)
      ]);

      setCourseReadings(freshReadings);
      setWeekReadingAssignments(freshAssignments);

      if (movingReadingId === assignmentId) {
        cancelMoveReading();
      }

      setSuccess('Reading deleted.');
    } catch (err) {
      console.error('Reading deletion failed:', err);
      setError(err?.message || 'Failed to delete reading.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div style={styles.muted}>Loading...</div>;

  return (
    <div>
      <style>
        {`
          @media (max-width: 760px) {
            .teaching-schedule-week-summary {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
      <h2 style={styles.subheading}>Teaching Schedule</h2>
      <p style={styles.muted}>
        Auto-populated from course configuration. Add descriptions, events, and readings per week.
      </p>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      <div style={styles.weekList}>
        {weeks.map(week => {
          const holiday = getHolidayForWeek(week);
          const weekMeetings = getMeetingsForWeek(week);
          const dueAssignments =
            isFirstActualClassOfWeek(week)
              ? getAssignmentsDueInWeek(week)
              : [];
          const isExpanded = expandedWeekId === week.id;
          const content = weekContent[week.id];
          const weekReadings = getWeekReadings(week.id);
          const weekEvents = getWeekEvents(week.id);
          const nr = newReading[week.id] || { title: '', author: '' };
          const ne = newEvents[week.id] || {
            title: '',
            event_date: '',
            event_time: '',
            location: ''
          };

          // Self-eval dates
          const classDate = new Date(week.class_date);
          const dayOfWeek = classDate.getDay();
          const monday = new Date(classDate);
          monday.setDate(classDate.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
          const sunday = new Date(monday);
          sunday.setDate(monday.getDate() + 6);

          const selfEvals = [];

          if (course.self_eval_enabled === true) {
            if (course.mid_eval_close_date) {
              const d = new Date(course.mid_eval_close_date);
              if (d >= monday && d <= sunday) {
                selfEvals.push({
                  label: 'Mid-Term Self-Evaluation',
                  date: course.mid_eval_close_date
                });
              }
            }

            if (course.final_eval_close_date) {
              const d = new Date(course.final_eval_close_date);
              if (d >= monday && d <= sunday) {
                selfEvals.push({
                  label: 'Final Self-Evaluation',
                  date: course.final_eval_close_date
                });
              }
            }
          }

          return (
            <div key={week.id} style={styles.weekItem}>
              <button
                style={styles.weekHeader}
                onClick={() => handleExpand(week.id)}
              >
                <div style={styles.weekHeaderLeft}>
  <div style={styles.weekNum}>
    Week {week.week_number}: <span style={styles.weekDate}>{formatDate(week.class_date)}</span>
  </div>
  {(holiday || weekMeetings.length > 0) && (
                    <div style={styles.weekPills}>
                      {holiday && (
                        <span style={styles.holidayPill}>
                          No Class: {holiday.reason}
                        </span>
                      )}

                      {weekMeetings.map(m => (
                        <span
                          key={m.id}
                          style={styles.meetingPill}
                        >
                          {m.meeting_type} Meetings
                        </span>
                      ))}
                    </div>
                  )}

                  {(weekReadings.length > 0 ||
                    weekEvents.length > 0 ||
                    dueAssignments.length > 0 ||
                    selfEvals.length > 0) && (
                    <div
                      className="teaching-schedule-week-summary"
                      style={styles.weekSummaryGrid}
                    >
                      {weekReadings.length > 0 && (
                        <div style={styles.weekSummaryColumn}>
                          <div
                            style={{
                              ...styles.weekSummaryHeading,
                              ...styles.readingSummaryHeading
                            }}
                          >
                            Readings
                          </div>

                          {weekReadings.map(assignment => {
                            const reading =
                              assignment.course_readings;

                            return (
                              <div
                                key={`reading-summary-${assignment.id}`}
                                style={{
                                  ...styles.weekSummaryItem,
                                  ...styles.readingSummaryItem
                                }}
                              >
                                <div
                                  style={{
                                    ...styles.weekSummaryTitle,
                                    ...styles.readingSummaryText
                                  }}
                                >
                                  {reading?.title || 'Reading'}
                                </div>

                                {reading?.author && (
                                  <div
                                    style={{
                                      ...styles.weekSummaryMeta,
                                      ...styles.readingSummaryText
                                    }}
                                  >
                                    {reading.author}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {weekEvents.length > 0 && (
                        <div style={styles.weekSummaryColumn}>
                          <div
                            style={{
                              ...styles.weekSummaryHeading,
                              ...styles.eventSummaryHeading
                            }}
                          >
                            Events
                          </div>

                          {weekEvents.map(event => {
                            const meta = [
                              event.event_date
                                ? formatDate(event.event_date)
                                : null,
                              event.event_time
                                ? String(event.event_time).slice(0, 5)
                                : null,
                              event.location || null
                            ].filter(Boolean);

                            return (
                              <div
                                key={`event-summary-${event.id}`}
                                style={{
                                  ...styles.weekSummaryItem,
                                  ...styles.eventSummaryItem
                                }}
                              >
                                <div
                                  style={{
                                    ...styles.weekSummaryTitle,
                                    ...styles.eventSummaryText
                                  }}
                                >
                                  {event.title || 'Event'}
                                </div>

                                {meta.length > 0 && (
                                  <div
                                    style={{
                                      ...styles.weekSummaryMeta,
                                      ...styles.eventSummaryText
                                    }}
                                  >
                                    {meta.join(' · ')}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {(dueAssignments.length > 0 ||
                        selfEvals.length > 0) && (
                        <div style={styles.weekSummaryColumn}>
                          <div
                            style={{
                              ...styles.weekSummaryHeading,
                              ...styles.assignmentSummaryHeading
                            }}
                          >
                            Assignments
                          </div>

                          {dueAssignments.map(a => {
                            const label =
                              getAssignmentScheduleLabel(
                                a,
                                courseAssignments
                              );

                            const assignmentDate =
                              getAssignmentScheduleDate(a);

                            const requiresSubmission =
                              a.assignment_types?.requires_submission ??
                              true;

                            return (
                              <div
                                key={
                                  a.instructor_assignment_occurrence_key ||
                                  a.id
                                }
                                style={{
                                  ...styles.weekSummaryItem,
                                  ...styles.assignmentSummaryItem
                                }}
                              >
                                <div
                                  style={{
                                    ...styles.weekSummaryTitle,
                                    ...styles.assignmentSummaryText
                                  }}
                                >
                                  {label}
                                </div>

                                {requiresSubmission &&
                                  assignmentDate && (
                                    <div
                                      style={{
                                        ...styles.weekSummaryMeta,
                                        ...styles.assignmentSummaryText
                                      }}
                                    >
                                      Due {formatDate(assignmentDate)}
                                    </div>
                                  )}
                              </div>
                            );
                          })}

                          {selfEvals.map((item, index) => (
                            <div
                              key={`self-eval-summary-${index}`}
                              style={{
                                ...styles.weekSummaryItem,
                                ...styles.assignmentSummaryItem
                              }}
                            >
                              <div
                                style={{
                                  ...styles.weekSummaryTitle,
                                  ...styles.assignmentSummaryText
                                }}
                              >
                                {item.label}
                              </div>

                              <div
                                style={{
                                  ...styles.weekSummaryMeta,
                                  ...styles.assignmentSummaryText
                                }}
                              >
                                Due {formatDate(item.date)}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {hasMeaningfulRichText(content?.description) && (
  <div style={styles.descPreview}>
    <span dangerouslySetInnerHTML={{ __html: content.description }} />
  </div>
)}
                </div>
                <span style={styles.chevron}>{isExpanded ? '▾' : '▸'}</span>
              </button>

              {isExpanded && (
                <div style={styles.weekBody}>
                  {(holiday || weekMeetings.length > 0 || weekEvents.length > 0 || dueAssignments.length > 0 || selfEvals.length > 0) && (
                    <div style={styles.autoInfo}>
                      {holiday && (
                        <div style={styles.autoRow}>
                          <span style={styles.autoIcon}>🚫</span>
                          <span><strong>No Class:</strong> {holiday.reason}</span>
                        </div>
                      )}
                      {weekMeetings.map(m => (
                        <div key={m.id} style={styles.autoRow}>
                          <span style={styles.autoIcon}>📅</span>
                          <span>
                            <strong>{m.meeting_type} Self-Evaluation Meetings:</strong> {formatDate(m.meeting_date)}
                          </span>
                        </div>
                      ))}
                      {weekEvents.map(event => {
                        const details = [
                          event.event_time
                            ? String(event.event_time).slice(0, 5)
                            : null,
                          event.location || null
                        ].filter(Boolean);

                        return (
                          <div key={event.id} style={styles.autoRow}>
                            <span style={styles.autoIcon}>📅</span>
                            <span>
                              <strong>{event.title}</strong>
                              {event.event_date
                                ? ` — ${formatDate(event.event_date)}`
                                : ''}
                              {details.length
                                ? `${event.event_date ? ', ' : ' — '}${details.join(' · ')}`
                                : ''}
                            </span>
                          </div>
                        );
                      })}
                      {dueAssignments.map(a => {
                        const label =
                          getAssignmentScheduleLabel(
                            a,
                            courseAssignments
                          );
                        const assignmentDate =
                          getAssignmentScheduleDate(
                            a
                          );
                        const requiresSubmission =
                          a.assignment_types?.requires_submission ?? true;
                        return (
                          <div key={
                          a
                            .instructor_assignment_occurrence_key ||
                          a.id
                        } style={styles.autoRow}>
                            <span style={styles.autoIcon}>📌</span>
                            <span>
                              {requiresSubmission
                                ? <><strong>{label}</strong> due, {formatDate(assignmentDate)}</>
                                : <strong>{label}</strong>
                              }
                            </span>
                          </div>
                        );
                      })}
                      {selfEvals.map((s, i) => (
                        <div key={i} style={styles.autoRow}>
                          <span style={styles.autoIcon}>📌</span>
                          <span><strong>{s.label}</strong> due, {formatDate(s.date)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={styles.section}>
                    <label style={styles.label}>Week settings</label>
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                      {[
  {
    field: 'attendance_enabled',
    label: 'Attendance',
    courseEnabled:
      course.attendance_enabled === true
  },
  {
    field: 'pd_enabled',
    label: 'PD',
    courseEnabled:
      course.pd_enabled === true
  },
  {
    field: 'cq_enabled',
    label: 'CQ',
    courseEnabled:
      course.cq_enabled === true
  },
  ...(
    course.qi_enabled
      ? [{
          field: 'qi_enabled',
          label: 'QI',
          courseEnabled: true
        }]
      : []
  )
].map(({
  field,
  label,
  courseEnabled
}) => {
  const disabled =
    !courseEnabled;

  return (
    <label
      key={field}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '14px',
        cursor:
          disabled
            ? 'not-allowed'
            : 'pointer',
        opacity:
          disabled
            ? 0.45
            : 1
      }}
    >
      <input
        type="checkbox"
        checked={
          courseEnabled &&
          !!week[field]
        }
        disabled={disabled}
        onChange={() =>
          handleToggleWeek(
            week.id,
            field,
            week[field]
          )
        }
      />

      {label}
    </label>
  );
})}
                    </div>
                  </div>
                  {/* TEACHING_SCHEDULE_THREE_COLUMN_EDITOR_20260919 */}
                  <div style={styles.editGrid}>
                    <div style={styles.section}>
                    <label style={styles.label}>Description</label>
                    <RichTextEditor
                      value={editDescriptions[week.id] || ''}
                      onChange={val => setEditDescriptions(prev => ({ ...prev, [week.id]: val }))}
                    />
                    <button
                      style={{ ...styles.btn, marginTop: '10px' }}
                      disabled={saving}
                      onClick={() => handleSaveDescription(week.id)}
                    >
                      {saving ? 'Saving...' : 'Save Description'}
                    </button>
                  </div>

                  <div style={styles.section}>
                    <label style={styles.label}>Events</label>

                    {weekEvents.length > 0 && (
                      <div style={styles.eventList}>
                        {weekEvents.map(event => (
                          <div key={event.id} style={styles.eventItem}>
                            {editingEventId === event.id ? (
                              <div style={styles.eventEditWrap}>
                                <div style={styles.eventGrid}>
                                  <div style={styles.field}>
                                    <label style={styles.smallLabel}>Title</label>
                                    <input
                                      style={styles.input}
                                      type="text"
                                      value={editingEvent?.title || ''}
                                      onChange={e =>
                                        setEditingEvent(prev => ({
                                          ...prev,
                                          title: e.target.value
                                        }))
                                      }
                                    />
                                  </div>

                                  <div style={styles.field}>
                                    <label style={styles.smallLabel}>Date (optional)</label>
                                                                        <CourseDatePicker
                                      value={
                                        editingEvent
                                          ?.event_date ||
                                        ''
                                      }
                                      onChange={
                                        value =>
                                          setEditingEvent(
                                            prev => ({
                                              ...prev,
                                              event_date:
                                                value
                                            })
                                          )
                                      }
                                      course={
                                        course
                                      }
                                      weeks={
                                        weeks
                                      }
                                      assignments={
                                        courseAssignments
                                      }
                                      typeSettings={
                                        calendarTypeSettings
                                      }
                                      scheduleItems={
                                        calendarScheduleContext
                                          .scheduleItems ||
                                        []
                                      }
                                      scheduleReadings={
                                        weekReadingAssignments
                                      }
                                      scheduleEvents={
                                        courseEvents
                                      }
                                      holidays={
                                        holidays
                                      }
                                    />
                                  </div>

                                  <div style={styles.field}>
                                    <label style={styles.smallLabel}>Time (optional)</label>
                                    <input
                                      style={styles.input}
                                      type="time"
                                      value={editingEvent?.event_time || ''}
                                      onChange={e =>
                                        setEditingEvent(prev => ({
                                          ...prev,
                                          event_time: e.target.value
                                        }))
                                      }
                                    />
                                  </div>

                                  <div style={styles.field}>
                                    <label style={styles.smallLabel}>Location (optional)</label>
                                    <input
                                      style={styles.input}
                                      type="text"
                                      value={editingEvent?.location || ''}
                                      onChange={e =>
                                        setEditingEvent(prev => ({
                                          ...prev,
                                          location: e.target.value
                                        }))
                                      }
                                    />
                                  </div>
                                </div>

                                <div style={styles.eventActions}>
                                  <button
                                    type="button"
                                    style={styles.btn}
                                    disabled={saving}
                                    onClick={() => handleSaveEvent(event.id)}
                                  >
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    style={styles.secondaryBtn}
                                    disabled={saving}
                                    onClick={cancelEditingEvent}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    style={styles.dangerBtn}
                                    disabled={saving}
                                    onClick={() => handleDeleteEvent(event)}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div style={styles.eventInfo}>
                                  <div style={styles.eventTitle}>
                                    {event.title}
                                  </div>
                                  {(event.event_date || event.event_time || event.location) && (
                                    <div style={styles.eventMeta}>
                                      {event.event_date
                                        ? formatDate(event.event_date)
                                        : ''}
                                      {event.event_time
                                        ? `${event.event_date ? ' · ' : ''}${String(event.event_time).slice(0, 5)}`
                                        : ''}
                                      {event.location
                                        ? `${event.event_date || event.event_time ? ' · ' : ''}${event.location}`
                                        : ''}
                                    </div>
                                  )}

                                  {movingEventId === event.id && (
                                    <div style={styles.moveRow}>
                                      <span style={styles.moveLabel}>Move to</span>
                                      <select
                                        style={styles.moveSelect}
                                        value={moveWeekTargets[`event-${event.id}`] || event.course_week_id}
                                        disabled={saving}
                                        onChange={e =>
                                          setMoveWeekTargets(prev => ({
                                            ...prev,
                                            [`event-${event.id}`]: e.target.value
                                          }))
                                        }
                                      >
                                        {weeks.map(targetWeek => (
                                          <option key={targetWeek.id} value={targetWeek.id}>
                                            Week {targetWeek.week_number} — {formatDate(targetWeek.class_date)}
                                          </option>
                                        ))}
                                      </select>
                                      <button
                                        type="button"
                                        style={styles.smallMoveBtn}
                                        disabled={saving}
                                        onClick={() => handleMoveEvent(event)}
                                      >
                                        Move
                                      </button>
                                      <button
                                        type="button"
                                        style={styles.smallCancelBtn}
                                        disabled={saving}
                                        onClick={cancelMoveEvent}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  )}
                                </div>

                                <div style={styles.eventActions}>
                                  <button
                                    type="button"
                                    style={styles.secondaryBtn}
                                    disabled={saving}
                                    onClick={() => beginMoveEvent(event)}
                                  >
                                    Move
                                  </button>
                                  <button
                                    type="button"
                                    style={styles.secondaryBtn}
                                    disabled={saving}
                                    onClick={() => startEditingEvent(event)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    style={styles.dangerBtn}
                                    disabled={saving}
                                    onClick={() => handleDeleteEvent(event)}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={styles.eventForm}>
                      <div style={styles.addReadingHeading}>Add event</div>

                      <div style={styles.eventGrid}>
                        <div style={styles.field}>
                          <label style={styles.smallLabel}>Title (required)</label>
                          <input
                            style={styles.input}
                            type="text"
                            value={ne.title}
                            onChange={e =>
                              setNewEvents(prev => ({
                                ...prev,
                                [week.id]: {
                                  ...ne,
                                  title: e.target.value
                                }
                              }))
                            }
                            placeholder="Event title"
                          />
                        </div>

                        <div style={styles.field}>
                          <label style={styles.smallLabel}>Date (optional)</label>
                                                    <CourseDatePicker
                            value={
                              ne.event_date
                            }
                            onChange={
                              value =>
                                setNewEvents(
                                  prev => ({
                                    ...prev,

                                    [week.id]: {
                                      ...ne,
                                      event_date:
                                        value
                                    }
                                  })
                                )
                            }
                            course={
                              course
                            }
                            weeks={
                              weeks
                            }
                            assignments={
                              courseAssignments
                            }
                            typeSettings={
                              calendarTypeSettings
                            }
                            scheduleItems={
                              calendarScheduleContext
                                .scheduleItems ||
                              []
                            }
                            scheduleReadings={
                              weekReadingAssignments
                            }
                            scheduleEvents={
                              courseEvents
                            }
                            holidays={
                              holidays
                            }
                          />
                        </div>

                        <div style={styles.field}>
                          <label style={styles.smallLabel}>Time (optional)</label>
                          <input
                            style={styles.input}
                            type="time"
                            value={ne.event_time || ''}
                            onChange={e =>
                              setNewEvents(prev => ({
                                ...prev,
                                [week.id]: {
                                  ...ne,
                                  event_time: e.target.value
                                }
                              }))
                            }
                          />
                        </div>

                        <div style={styles.field}>
                          <label style={styles.smallLabel}>Location (optional)</label>
                          <input
                            style={styles.input}
                            type="text"
                            value={ne.location || ''}
                            onChange={e =>
                              setNewEvents(prev => ({
                                ...prev,
                                [week.id]: {
                                  ...ne,
                                  location: e.target.value
                                }
                              }))
                            }
                            placeholder="Location"
                          />
                        </div>
                      </div>

                      <button
                        type="button"
                        style={styles.btn}
                        disabled={saving || !ne.title?.trim()}
                        onClick={() => handleAddEvent(week)}
                      >
                        Add Event
                      </button>
                    </div>
                  </div>

                  <div
                    style={{
                      ...styles.section,
                      ...styles.readingSection
                    }}
                  >
                    <label style={styles.label}>Readings</label>

                    {weekReadings.length > 0 && (
                      <div style={styles.readingList}>
                        <div style={styles.currentReadingHeading}>
                          Current readings
                        </div>
                        {weekReadings.map(assignment => {
                          const reading = assignment.course_readings;
                          return (
                            <div key={assignment.id} style={styles.readingItem}>
                                                            <div style={styles.readingInfo}>
                                {editingReadingId === assignment.id ? (
                                  <div style={styles.readingEditBox}>
                                    <div style={styles.readingEditGrid}>
                                      <div style={styles.field}>
                                        <label style={styles.smallLabel}>
                                          Title
                                        </label>
                                        <input
                                          style={styles.input}
                                          value={editingReading?.title || ''}
                                          onChange={event =>
                                            setEditingReading(previous => ({
                                              ...previous,
                                              title: event.target.value
                                            }))
                                          }
                                        />
                                      </div>

                                      <div style={styles.field}>
                                        <label style={styles.smallLabel}>
                                          Author
                                        </label>
                                        <input
                                          style={styles.input}
                                          value={editingReading?.author || ''}
                                          onChange={event =>
                                            setEditingReading(previous => ({
                                              ...previous,
                                              author: event.target.value
                                            }))
                                          }
                                        />
                                      </div>
                                    </div>

                                    <div style={styles.eventActions}>
                                      <button
                                        type="button"
                                        style={styles.smallMoveBtn}
                                        disabled={
                                          saving ||
                                          !editingReading?.title?.trim()
                                        }
                                        onClick={handleSaveReading}
                                      >
                                        Save
                                      </button>

                                      <button
                                        type="button"
                                        style={styles.smallCancelBtn}
                                        disabled={saving}
                                        onClick={cancelEditingReading}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <span style={styles.readingTitle}>
                                    {reading?.drive_file_id ? (
                                      <a
                                        href={`https://drive.google.com/file/d/${reading.drive_file_id}/preview`}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={styles.readingLink}
                                      >
                                        {reading?.title}
                                        {reading?.author
                                          ? ` by ${reading.author}`
                                          : ''}
                                      </a>
                                    ) : (
                                      <span>
                                        {reading?.title}
                                        {reading?.author
                                          ? ` by ${reading.author}`
                                          : ''}
                                      </span>
                                    )}
                                  </span>
                                )}

                                {movingReadingId === assignment.id && (
                                  <div style={styles.moveRow}>
                                    <span style={styles.moveLabel}>Move to</span>
                                    <select
                                      style={styles.moveSelect}
                                      value={moveWeekTargets[`reading-${assignment.id}`] || assignment.course_week_id}
                                      disabled={saving}
                                      onChange={e =>
                                        setMoveWeekTargets(prev => ({
                                          ...prev,
                                          [`reading-${assignment.id}`]: e.target.value
                                        }))
                                      }
                                    >
                                      {weeks.map(targetWeek => (
                                        <option key={targetWeek.id} value={targetWeek.id}>
                                          Week {targetWeek.week_number} — {formatDate(targetWeek.class_date)}
                                        </option>
                                      ))}
                                    </select>
                                    <button
                                      type="button"
                                      style={styles.smallMoveBtn}
                                      disabled={saving}
                                      onClick={() => handleMoveReading(assignment)}
                                    >
                                      Move
                                    </button>
                                    <button
                                      type="button"
                                      style={styles.smallCancelBtn}
                                      disabled={saving}
                                      onClick={cancelMoveReading}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                )}
                              </div>
                              <div style={styles.eventActions}>
                                <button
                                  type="button"
                                  style={styles.secondaryBtn}
                                  disabled={
                                    saving ||
                                    editingReadingId === assignment.id
                                  }
                                  onClick={() =>
                                    beginEditingReading(assignment)
                                  }
                                >
                                  Edit
                                </button>

                                <button
                                  type="button"
                                  style={styles.secondaryBtn}
                                  disabled={
                                    saving ||
                                    editingReadingId === assignment.id
                                  }
                                  onClick={() => beginMoveReading(assignment)}
                                >
                                  Move
                                </button>
                                <button
                                  type="button"
                                  style={styles.dangerBtn}
                                  disabled={saving}
                                  onClick={() => handleRemoveReading(assignment.id)}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div style={styles.addReadingForm}>
                      <div style={styles.addReadingHeading}>Upload new reading</div>
                      <div style={styles.addReadingRow}>
                        <div style={styles.field}>
                          <label style={styles.smallLabel}>Title (required)</label>
                          <input
                            style={styles.input}
                            type="text"
                            value={nr.title}
                            onChange={e => setNewReading(prev => ({
                              ...prev,
                              [week.id]: { ...prev[week.id], title: e.target.value }
                            }))}
                            placeholder="Reading title"
                          />
                        </div>
                        <div style={styles.field}>
                          <label style={styles.smallLabel}>Author (optional)</label>
                          <input
                            style={styles.input}
                            type="text"
                            value={nr.author || ''}
                            onChange={e => setNewReading(prev => ({
                              ...prev,
                              [week.id]: { ...prev[week.id], author: e.target.value }
                            }))}
                            placeholder="Author name"
                          />
                        </div>
                      </div>
                      <label style={styles.uploadLabel}>
                        <input
                          type="file"
                          style={{ display: 'none' }}
                          onChange={e => handleUploadReading(week.id, e.target.files[0])}
                          disabled={uploading[week.id]}
                        />
                        <span style={{
                          ...styles.uploadBtn,
                          opacity: nr.title?.trim() ? 1 : 0.5
                        }}>
                          {uploading[week.id] ? 'Uploading...' : '↑ Upload file & add reading'}
                        </span>
                      </label>
                      {!nr.title?.trim() && (
                        <div style={styles.uploadHint}>Enter a title first</div>
                      )}
                    </div>

                    <div style={styles.importForm}>
                      <div style={styles.addReadingHeading}>Add from another course</div>
                      <div style={styles.importRow}>
                        <select
                          style={styles.input}
                          value={importCourseId[week.id] || ''}
                          onChange={e => handleLoadImportCourse(week.id, e.target.value)}
                        >
                          <option value="">Select a course...</option>
                          {allCourses.map(c => (
                            <option key={c.id} value={c.id}>
                              {c.course_titles?.name || c.name || 'Untitled'}
                            </option>
                          ))}
                        </select>
                        {importCourseId[week.id] && (
                          <>
                            {loadingImport[week.id] ? (
                              <div style={styles.muted}>Loading...</div>
                            ) : (
                              <>
                                <select
                                  style={styles.input}
                                  value={importReadingId[week.id] || ''}
                                  onChange={e => setImportReadingId(prev => ({ ...prev, [week.id]: e.target.value }))}
                                >
                                  <option value="">Select a reading...</option>
                                  {(importCourseReadings[week.id] || []).map(r => (
                                    <option key={r.id} value={r.id}>
                                      {r.title}{r.author ? ` (${r.author})` : ''}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  style={styles.btn}
                                  disabled={!importReadingId[week.id] || saving}
                                  onClick={() => handleImportReading(week.id, importReadingId[week.id])}
                                >
                                  Add
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {courseReadings.length > 0 && (
                      <div style={styles.importForm}>
                        <div style={styles.addReadingHeading}>Add from this course's readings</div>
                        <div style={styles.importRow}>
                          <select
                            style={styles.input}
                            value=""
                            onChange={async e => {
                              if (!e.target.value) return;
                              const readingId = e.target.value;
                              const weekAssignments = weekReadingAssignments.filter(a => a.course_week_id === week.id);
                              const already = weekAssignments.find(a => a.course_reading_id === readingId);
                              if (already) { setError('Already assigned to this week.'); return; }
                              setSaving(true);
                              try {
                                const result = await assignReadingToWeek(week.id, readingId, weekAssignments.length + 1);
                                setWeekReadingAssignments(prev => [...prev, result[0]]);
                                setSuccess('Reading added.');
                              } catch (err) {
                                setError('Failed to add reading.');
                              } finally {
                                setSaving(false);
                              }
                            }}
                          >
                            <option value="">Select a reading to add...</option>
                            {courseReadings
                              .filter(r => 
  weekReadingAssignments.some(a => a.course_reading_id === r.id) &&
  !weekReadingAssignments.find(a => a.course_week_id === week.id && a.course_reading_id === r.id)
)
                              .map(r => (
                                <option key={r.id} value={r.id}>
                                  {r.title}{r.author ? ` (${r.author})` : ''}
                                </option>
                              ))
                            }
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  subheading: { margin: '0 0 8px', fontSize: '18px', color: '#1f2933' },
  muted: { color: '#66788a', fontSize: '14px', margin: '0 0 12px', lineHeight: '1.5' },
  error: {
    background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '8px',
    padding: '10px 14px', color: '#a12a2a', fontSize: '14px', marginBottom: '16px'
  },
  success: {
    background: '#e7f5ed', border: '1px solid #b9dfc9', borderRadius: '8px',
    padding: '10px 14px', color: '#216e45', fontSize: '14px', marginBottom: '16px'
  },
  weekList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  weekItem: {
    border: '1px solid #d7dee5', borderRadius: '10px', overflow: 'hidden', background: 'white'
  },
  weekHeader: {
    width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '14px 16px', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left'
  },
  weekHeaderLeft: { flex: 1 },
  weekNum: { fontWeight: '700', fontSize: '14px', color: '#1f2933', marginBottom: '2px' },
  weekDate: { fontSize: '13px', color: '#66788a', marginBottom: '4px' },
  weekPills: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
    marginBottom: '10px'
  },
  weekSummaryGrid: {
    display: 'grid',
    gridTemplateColumns:
      'repeat(3, minmax(0, 1fr))',
    gap: '10px',
    marginTop: '8px'
  },
  weekSummaryColumn: {
    minWidth: 0,
    border: '1px solid #e1e7eb',
    borderRadius: '8px',
    padding: '8px 9px',
    background: '#fbfcfd'
  },
  weekSummaryHeading: {
    fontSize: '10px',
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '5px'
  },
  weekSummaryItem: {
    padding: '4px 7px',
    borderRadius: '7px',
    marginBottom: '4px'
  },
  weekSummaryTitle: {
    fontSize: '11px',
    fontWeight: '700',
    lineHeight: 1.25
  },
  weekSummaryMeta: {
    marginTop: '1px',
    fontSize: '9px',
    lineHeight: 1.25,
    opacity: 0.88
  },
  readingSummaryHeading: {
    color: '#216e45'
  },
  readingSummaryItem: {
    background: '#e7f5ed',
    border: '1px solid #c6e5d2'
  },
  readingSummaryText: {
    color: '#216e45'
  },
  eventSummaryHeading: {
    color: '#5b3fa3'
  },
  eventSummaryItem: {
    background: '#ede9fe',
    border: '1px solid #d8d0f4'
  },
  eventSummaryText: {
    color: '#5b3fa3'
  },
  assignmentSummaryHeading: {
    color: '#8a5b00'
  },
  assignmentSummaryItem: {
    background: '#fff4d7',
    border: '1px solid #f0ddb0'
  },
  assignmentSummaryText: {
    color: '#8a5b00'
  },
  weekSummaryEmpty: {
    color: '#9aa7b2',
    fontSize: '11px',
    fontStyle: 'italic'
  },
  descPreview: {
  fontSize: '13px', color: '#1f2933', lineHeight: '1.4', marginTop: '4px',
  maxHeight: '60px', overflow: 'hidden'
},
  holidayPill: {
    background: '#faeaea', color: '#a12a2a', borderRadius: '999px',
    padding: '2px 8px', fontSize: '11px', fontWeight: '700'
  },
  meetingPill: {
    background: '#e7f1f7', color: '#215b7d', borderRadius: '999px',
    padding: '2px 8px', fontSize: '11px', fontWeight: '700'
  },
  duePill: {
    background: '#fff4d7', color: '#8a5b00', borderRadius: '999px',
    padding: '2px 8px', fontSize: '11px', fontWeight: '700'
  },
  eventPill: {
    background: '#ede9fe', color: '#5b3fa3', borderRadius: '999px',
    padding: '2px 8px', fontSize: '11px', fontWeight: '700'
  },
  readingPill: {
    background: '#e7f5ed', color: '#216e45', borderRadius: '999px',
    padding: '2px 8px', fontSize: '11px', fontWeight: '700'
  },
  chevron: { color: '#66788a', fontSize: '16px', flexShrink: 0 },
  weekBody: { padding: '16px', borderTop: '1px solid #d7dee5' },

  editGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: '18px',
    alignItems: 'start'
  },
  autoInfo: { marginBottom: '16px' },
  autoRow: {
    display: 'flex', alignItems: 'flex-start', gap: '8px',
    padding: '8px 12px', background: '#f7f9fa', borderRadius: '8px',
    marginBottom: '6px', fontSize: '14px', color: '#1f2933'
  },
  autoIcon: { flexShrink: 0 },
  section: {
    marginBottom: '20px',
    minWidth: 0
  },
  label: { display: 'block', fontWeight: '700', fontSize: '13px', marginBottom: '8px', color: '#1f2933' },
  smallLabel: { display: 'block', fontWeight: '600', fontSize: '12px', marginBottom: '4px', color: '#66788a' },
  btn: {
    background: '#215b7d', color: 'white', border: 'none', borderRadius: '8px',
    padding: '8px 14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
    whiteSpace: 'nowrap'
  },
  input: {
    width: '100%', border: '1px solid #d7dee5', borderRadius: '8px',
    padding: '8px 10px', fontSize: '14px', boxSizing: 'border-box', background: 'white'
  },
  eventList: { marginBottom: '12px' },
  eventItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '10px',
    padding: '10px 12px',
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    marginBottom: '6px',
    background: '#f7f9fa'
  },
  eventInfo: { flex: 1, minWidth: 0 },
  eventTitle: { fontWeight: '700', fontSize: '14px', color: '#1f2933' },
  eventMeta: { marginTop: '2px', fontSize: '12px', color: '#66788a' },
  eventActions: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
    flexWrap: 'wrap',
    flexShrink: 0
  },
  eventEditWrap: { width: '100%' },
  eventForm: {
    border: '1px solid #d7dee5', borderRadius: '8px', padding: '12px',
    background: 'white'
  },
  eventGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '10px',
    marginBottom: '10px'
  },
  secondaryBtn: {
    background: 'white', color: '#215b7d', border: '1px solid #9fb7c7',
    borderRadius: '8px', padding: '7px 12px', fontSize: '13px',
    fontWeight: '600', cursor: 'pointer'
  },
  moveRow: {
    display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
    marginTop: '8px'
  },
  moveLabel: { fontSize: '12px', fontWeight: '700', color: '#66788a' },
  moveSelect: {
    minWidth: '210px', border: '1px solid #d7dee5', borderRadius: '6px',
    padding: '6px 8px', fontSize: '12px', background: 'white'
  },
  smallMoveBtn: {
    background: '#215b7d', color: 'white', border: 'none', borderRadius: '6px',
    padding: '6px 10px', fontSize: '12px', fontWeight: '600', cursor: 'pointer'
  },
  smallCancelBtn: {
    background: 'white', color: '#66788a', border: '1px solid #c8d1d8', borderRadius: '6px',
    padding: '6px 10px', fontSize: '12px', fontWeight: '600', cursor: 'pointer'
  },
  readingSection: {
    display: 'flex',
    flexDirection: 'column'
  },
  readingList: {
    marginBottom: '12px',
    marginTop: '2px',
    order: 10
  },
  currentReadingHeading: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#66788a',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '8px'
  },
  readingItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '8px',
    padding: '10px 12px',
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    marginBottom: '6px',
    background: '#f7f9fa'
  },
  readingInfo: { fontSize: '14px', color: '#1f2933', flex: 1 },
  readingTitle: { fontWeight: '600' },
  readingLink: { color: '#215b7d', textDecoration: 'underline' },
  addReadingForm: {
    border: '1px solid #d7dee5', borderRadius: '8px', padding: '12px',
    background: 'white', marginBottom: '10px', order: 1
  },
  importForm: {
    border: '1px solid #d7dee5', borderRadius: '8px', padding: '12px',
    background: 'white', marginBottom: '10px', order: 2
  },
  readingEditBox: {
    display: 'grid',
    gap: '8px'
  },
  readingEditGrid: {
    display: 'grid',
    gridTemplateColumns:
      'repeat(auto-fit, minmax(min(160px, 100%), 1fr))',
    gap: '8px'
  },
  addReadingHeading: {
    fontSize: '12px', fontWeight: '700', color: '#66788a',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px'
  },
  addReadingRow: { display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '10px' },
  importRow: { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' },
  field: {
    flex: 1,
    minWidth: 0
  },
  uploadLabel: { cursor: 'pointer', display: 'inline-block' },
  uploadBtn: {
    background: '#215b7d', color: 'white', borderRadius: '8px',
    padding: '8px 14px', fontSize: '13px', fontWeight: '600',
    display: 'inline-block', cursor: 'pointer'
  },
  uploadHint: { fontSize: '12px', color: '#a12a2a', marginTop: '6px' },
  dangerBtn: {
    background: 'transparent', color: '#a12a2a', border: '1px solid #e2b8b8',
    borderRadius: '6px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer',
    flexShrink: 0
  }
};